import { useState } from 'react';
import { Button, DataTable, Modal, Pager, StatusPill, type DataTableColumn } from '../../components';
import { formatCents, formatCount } from '../../lib/format/money';
import { formatDate } from '../../lib/format/dates';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { DishDetail } from '../dishes/DishDetail';
import { humanize, orderStatusTone, stringParam } from '../profiles/people';
import type { OrderSearchRow } from './api';
import { Section } from './detailParts';
import { OrderDetail } from './OrderDetail';
import { useOrderDetail, useOrderSearch } from './queries';

/**
 * The Dish and Order dialogs, addressed by the URL (`?dish=<listingId>`,
 * `?order=<orderId>`) so they stack over whatever opened them — a chef or
 * eater dialog, or each other — and survive a reload.
 *
 *   Dish dialog:  Dish Detail, then the dish's orders (click → Order dialog)
 *   Order dialog: Order Detail, then the Dish Detail of what was ordered
 *
 * A screen renders <DetailDialogs /> once; anything inside it opens them with
 * useDetailLinks().
 */
export function useDetailLinks() {
  const { setParams } = useTableUrlState();
  return {
    openDish: (listingId: string) => setParams({ dish: listingId }),
    openOrder: (orderId: string) => setParams({ order: orderId }),
  };
}

export function DetailDialogs() {
  const table = useTableUrlState();
  const dishId = stringParam(table.filters.dish);
  const orderId = stringParam(table.filters.order);

  return (
    <>
      {dishId && (
        <DishDialog
          listingId={dishId}
          onOpenOrder={(id) => table.setParams({ order: id })}
          onClose={() => table.setParams({ dish: undefined })}
        />
      )}
      {/* After the dish dialog, so an order opened from a dish sits on top. */}
      {orderId && <OrderDialog orderId={orderId} onClose={() => table.setParams({ order: undefined })} />}
    </>
  );
}

function DialogFooter({ onClose }: { onClose: () => void }) {
  return (
    <div className="mt-[18px] flex justify-end border-t border-[rgba(143,68,32,.1)] pt-4">
      <Button variant="ghost-dark" size="sm" onClick={onClose}>
        Close
      </Button>
    </div>
  );
}

export function DishDialog({
  listingId,
  onOpenOrder,
  onClose,
}: {
  listingId: string;
  onOpenOrder: (orderId: string) => void;
  onClose: () => void;
}) {
  return (
    <Modal isOpen onClose={onClose} size="xl" layer="top">
      <DishDetail listingId={listingId} />
      <DishOrders listingId={listingId} onOpenOrder={onOpenOrder} />
      <DialogFooter onClose={onClose} />
    </Modal>
  );
}

export function OrderDialog({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  return (
    <Modal isOpen onClose={onClose} size="xl" layer="top">
      <OrderDetail orderId={orderId} />
      <OrderDish orderId={orderId} />
      <DialogFooter onClose={onClose} />
    </Modal>
  );
}

/** The dish an order was for, below the order. */
function OrderDish({ orderId }: { orderId: string }) {
  // Same query as OrderDetail above, so it reads the cache — no second request.
  const detail = useOrderDetail(orderId);
  const listingId = detail.data?.order.listingId;
  if (!listingId) return null;
  return (
    <div className="mt-6 border-t border-[rgba(143,68,32,.14)] pt-5">
      <DishDetail listingId={listingId} />
    </div>
  );
}

const ORDER_COLUMNS: DataTableColumn<OrderSearchRow>[] = [
  {
    id: 'order',
    header: 'Order',
    width: '104px',
    cell: (r) => <span className="font-mono text-xs">{r.orderNumber}</span>,
  },
  {
    id: 'eater',
    header: 'Eater',
    width: '1fr',
    cell: (r) => <span className="truncate">{r.eaterDisplayName || '—'}</span>,
  },
  {
    id: 'date',
    header: 'Date',
    width: '100px',
    cell: (r) => <span className="text-muted">{formatDate(r.orderedAt)}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    // Fixed track sized to the widest status pill ("Ready to pickup").
    width: '128px',
    cell: (r) => <StatusPill tone={orderStatusTone(r.status)}>{humanize(r.status)}</StatusPill>,
  },
  { id: 'qty', header: 'Qty', width: '40px', align: 'right', numeric: true, cell: (r) => formatCount(r.quantity) },
  { id: 'total', header: 'Total', width: '76px', align: 'right', numeric: true, cell: (r) => formatCents(r.totalPaid) },
];

const PAGE_SIZE = 10;

function DishOrders({ listingId, onOpenOrder }: { listingId: string; onOpenOrder: (orderId: string) => void }) {
  const [page, setPage] = useState(1);
  const orders = useOrderSearch({ byListingId: listingId, page, pageSize: PAGE_SIZE });
  return (
    <Section title="Orders">
      <DataTable
        label="Orders for this dish"
        compact
        minWidth={0}
        columns={ORDER_COLUMNS}
        rows={orders.data?.items ?? []}
        getRowId={(r) => r.orderId}
        onRowClick={(r) => onOpenOrder(r.orderId)}
        loading={orders.isPending}
        error={orders.error}
        onRetry={() => void orders.refetch()}
        emptyLabel="No orders for this dish yet."
      />
      {orders.data && orders.data.total > PAGE_SIZE && (
        <Pager page={page} pageSize={PAGE_SIZE} total={orders.data.total} onPageChange={setPage} noun="orders" />
      )}
    </Section>
  );
}
